import React from 'react';
import { Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const LEAD_STAGES = [
  { id: 'new_lead', label: 'New Lead' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'office_visit_meeting', label: 'Office Visit / Meeting' },
  { id: 'site_visit', label: 'Site Visit' },
  { id: 'interested', label: 'Interested' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'booking', label: 'Booking' },
  { id: 'documentation', label: 'Documentation' },
  { id: 'closed', label: 'Closed' },
];

const ReportsTab = ({ API, handleExportExcel, exporting, leadsRefreshKey = 0, globalProject = 'all' }) => {
  const [reportsData, setReportsData] = React.useState(null);
  const [reportsLoading, setReportsLoading] = React.useState(true);
  const [selectedReportProject, setSelectedReportProject] = React.useState('all');

  React.useEffect(() => {
    setSelectedReportProject(globalProject || 'all');
  }, [globalProject]);

  React.useEffect(() => {
    const loadReports = async () => {
      setReportsLoading(true);
      try {
        const query = selectedReportProject === 'all' ? '' : `?project=${selectedReportProject}`;
        const res = await fetch(`${API}/marketing-dashboard/reports/analysis${query}`);
        const data = await res.json();
        setReportsData(data);
      } catch (err) {
      } finally {
        setReportsLoading(false);
      }
    };
    loadReports();
  }, [API, selectedReportProject, leadsRefreshKey]);

  if (reportsLoading) return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
    </div>
  );
  if (!reportsData) return <p className="text-center py-8 text-stone-400">No data available</p>;

  const {
    projectBreakdown,
    statusDistribution,
    remarkBreakdown,
    monthlyTrend,
    dmPerformance,
    referenceSource,
    nagpurPhaseBreakdown,
    totalLeads,
    dateWiseTrend = [],
    addressBreakdown = [],
    segmentBreakdown = [],
    stageIndex = [],
    requirementConfigBreakdown = [],
    agentBreakdown = [],
  } = reportsData;

  const statusColors = { INPROCESS: '#b45309', HOLD: '#1d4ed8', CANCEL: '#b91c1c', DONE: '#15803d' };
  const CHART_COLORS = ['#1d4ed8', '#6d28d9', '#b45309', '#15803d', '#b91c1c', '#4338ca', '#be185d', '#0f766e', '#c2410c'];
  const stageColumns = LEAD_STAGES.map((stage) => ({ key: stage.id, label: stage.label }));
  const stageTotals = stageColumns.reduce((acc, stage) => {
    acc[stage.key] = stageIndex.reduce((sum, row) => sum + (row[stage.key] || 0), 0);
    return acc;
  }, {});
  const dateWiseChartData = [...dateWiseTrend].sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 45);
  const addressChartData = addressBreakdown.slice(0, 12).map((item) => ({
    ...item,
    shortAddress: item.address?.length > 18 ? `${item.address.slice(0, 18)}..` : item.address,
  }));
  const segmentChartData = segmentBreakdown.slice(0, 12);
  const requirementChartData = requirementConfigBreakdown.slice(0, 12);
  const agentChartData = agentBreakdown.slice(0, 12).map((item) => ({
    ...item,
    shortName: item.name?.length > 16 ? `${item.name.slice(0, 16)}..` : item.name,
  }));

  return (
    <div className="space-y-6">
      {/* Export Actions */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <h3 className="text-lg font-bold text-stone-800">Reports & Data Analysis</h3>
        <div className="flex flex-wrap gap-2 items-center">
          <Button data-testid="download-full-excel" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={handleExportExcel} disabled={exporting}>
            <Download className="w-4 h-4 mr-2" /> {exporting ? 'Exporting...' : 'Download Full Excel Report'}
          </Button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statusDistribution.map((s) => (
          <Card key={s.status} className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">{s.status}</p>
                  <p className="text-2xl font-bold mt-1" style={{ color: statusColors[s.status] || '#64748b' }}>
                    {s.count.toLocaleString()}
                  </p>
                  <p className="text-xs text-stone-400">{totalLeads ? (s.count / totalLeads * 100).toFixed(1) : 0}% of total</p>
                </div>
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: (statusColors[s.status] || '#64748b') + '20' }}>
                  <span className="text-lg font-bold" style={{ color: statusColors[s.status] }}>{(s.count / totalLeads * 100).toFixed(0)}%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* All Sites Stage Index */}
      <Card className="border-0 shadow-md overflow-hidden" data-testid="marketing-stage-index-card">
        <CardHeader className="bg-gradient-to-r from-teal-700 to-teal-900 text-white py-3">
          <CardTitle className="text-sm font-semibold">All Sites Stage Index</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="marketing-stage-index-table">
              <thead>
                <tr className="bg-stone-100">
                  <th className="px-3 py-2 text-left font-semibold text-stone-700">Project</th>
                  {stageColumns.map((stage) => (
                    <th key={stage.key} className="px-3 py-2 text-right font-semibold text-stone-700 whitespace-nowrap">{stage.label}</th>
                  ))}
                  <th className="px-3 py-2 text-right font-semibold text-stone-700">Total</th>
                </tr>
              </thead>
              <tbody>
                {stageIndex.length > 0 ? stageIndex.map((row) => (
                  <tr key={row.projectId} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-3 py-2 font-medium text-stone-800 whitespace-nowrap">{row.project}</td>
                    {stageColumns.map((stage) => (
                      <td key={stage.key} className="px-3 py-2 text-right">{(row[stage.key] || 0).toLocaleString()}</td>
                    ))}
                    <td className="px-3 py-2 text-right font-bold text-teal-800">{row.total?.toLocaleString()}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={stageColumns.length + 2} className="px-3 py-8 text-center text-stone-500">No stage index data available for the selected project.</td>
                  </tr>
                )}
                {stageIndex.length > 0 && (
                  <tr className="bg-teal-900 text-white font-bold">
                    <td className="px-3 py-2">TOTAL</td>
                    {stageColumns.map((stage) => (
                      <td key={stage.key} className="px-3 py-2 text-right">{(stageTotals[stage.key] || 0).toLocaleString()}</td>
                    ))}
                    <td className="px-3 py-2 text-right">{stageIndex.reduce((sum, row) => sum + (row.total || 0), 0).toLocaleString()}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Date-wise + Address-wise */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="border-0 shadow-md overflow-hidden" data-testid="marketing-date-wise-table-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-stone-800">Date-wise Lead Table</CardTitle></CardHeader>
          <CardContent className="p-0">
            {dateWiseChartData.length > 0 ? (
              <div className="overflow-auto max-h-[360px]">
                <table className="w-full text-xs" data-testid="marketing-date-wise-table">
                  <thead className="sticky top-0 bg-stone-100 z-10">
                    <tr>
                      <th className="px-2 py-2 text-left font-semibold text-stone-700 whitespace-nowrap">Date</th>
                      <th className="px-2 py-2 text-right font-semibold text-stone-700">Leads</th>
                      <th className="px-2 py-2 text-right font-semibold text-violet-600">Office Visits</th>
                      <th className="px-2 py-2 text-right font-semibold text-indigo-600">Project Visits</th>
                      <th className="px-2 py-2 text-right font-semibold text-amber-600">Scope</th>
                      <th className="px-2 py-2 text-right font-semibold text-emerald-600">Bookings</th>
                      <th className="px-2 py-2 text-right font-semibold text-teal-600">Confirm Sale</th>
                      <th className="px-2 py-2 text-right font-semibold text-rose-600">Registry</th>
                      <th className="px-2 py-2 text-right font-semibold text-blue-600">Possession</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dateWiseChartData.map((d, i) => (
                      <tr key={i} className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="px-2 py-1.5 font-medium text-stone-800 whitespace-nowrap">{d.date}</td>
                        <td className="px-2 py-1.5 text-right font-bold">{(d.total || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right text-violet-600">{(d.officeVisit || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right text-indigo-600">{(d.projectVisit || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right text-amber-600">{(d.scope || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right text-emerald-600 font-semibold">{(d.bookings || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right text-teal-600">{(d.confirmSale || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right text-rose-600">{(d.registry || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right text-blue-600">{(d.possession || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-sm text-stone-500">No date-wise data available for this project.</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md" data-testid="marketing-address-wise-chart-card">
          <CardHeader className="pb-1"><CardTitle className="text-sm font-semibold text-stone-800">Address-wise Count Graph</CardTitle></CardHeader>
          <CardContent>
            {addressChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={addressChartData} margin={{ left: 10, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="shortAddress" tick={{ fontSize: 9, fill: '#64748b' }} angle={-35} textAnchor="end" height={70} interval={0} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                  <Tooltip formatter={(value) => [value, 'Count']} labelFormatter={(label, payload) => payload?.[0]?.payload?.address || label} />
                  <Bar isAnimationActive={false} dataKey="count" fill="#14b8a6" name="Count" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-sm text-stone-500">No address-wise data available for this project.</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Segment (land) OR Requirement/Config (flat) */}
      {['dream_kokan', 'dream_nagpur'].includes(selectedReportProject) ? (
        <Card className="border-0 shadow-md" data-testid="marketing-segment-wise-chart-card">
          <CardHeader className="pb-1"><CardTitle className="text-sm font-semibold text-stone-800">Segment-wise Graph (Gunthe)</CardTitle></CardHeader>
          <CardContent>
            {segmentChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={segmentChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="segment" tick={{ fontSize: 9, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                  <Tooltip />
                  <Bar isAnimationActive={false} dataKey="count" fill="#f97316" name="Count" radius={[4, 4, 0, 0]}>
                    {segmentChartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-sm text-stone-500">No gunthe / segment data available for this project.</div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-md" data-testid="marketing-requirement-config-chart-card">
          <CardHeader className="pb-1"><CardTitle className="text-sm font-semibold text-stone-800">Requirement / Configuration Graph</CardTitle></CardHeader>
          <CardContent>
            {requirementChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={requirementChartData} margin={{ left: 10, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#64748b' }} angle={-35} textAnchor="end" height={70} interval={0} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                  <Tooltip />
                  <Bar isAnimationActive={false} dataKey="count" fill="#8b5cf6" name="Count" radius={[4, 4, 0, 0]}>
                    {requirementChartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-sm text-stone-500">No requirement / configuration data available for this project.</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Agent + Reference */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="border-0 shadow-md" data-testid="marketing-agent-wise-chart-card">
          <CardHeader className="pb-1"><CardTitle className="text-sm font-semibold text-stone-800">Agent-wise Graph</CardTitle></CardHeader>
          <CardContent>
            {agentChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={agentChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="shortName" tick={{ fontSize: 9, fill: '#64748b' }} angle={-35} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                  <Tooltip formatter={(value) => [value, 'Count']} labelFormatter={(label, payload) => payload?.[0]?.payload?.name || label} />
                  <Bar isAnimationActive={false} dataKey="count" fill="#0ea5e9" name="Count" radius={[4, 4, 0, 0]}>
                    {agentChartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-sm text-stone-500">No agent-wise data available for this project.</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md" data-testid="marketing-reference-wise-chart-card">
          <CardHeader className="pb-1"><CardTitle className="text-sm font-semibold text-stone-800">Reference-wise Graph</CardTitle></CardHeader>
          <CardContent>
            {referenceSource.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={referenceSource} margin={{ left: 10, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="source" tick={{ fontSize: 9, fill: '#64748b' }} angle={-35} textAnchor="end" height={70} interval={0} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                  <Tooltip />
                  <Bar isAnimationActive={false} dataKey="count" fill="#ec4899" name="Count" radius={[4, 4, 0, 0]}>
                    {referenceSource.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-sm text-stone-500">No reference-wise data available for this project.</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Project Analysis Table + Chart */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="border-0 shadow-md overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-stone-800 to-stone-700 text-white py-3">
            <CardTitle className="text-sm font-semibold">Project-wise Lead Analysis</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-stone-100">
                    <th className="px-3 py-2 text-left font-semibold text-stone-700">Project</th>
                    <th className="px-3 py-2 text-right font-semibold">Leads</th>
                    <th className="px-3 py-2 text-right font-semibold text-violet-600">OV</th>
                    <th className="px-3 py-2 text-right font-semibold text-indigo-600">PV</th>
                    <th className="px-3 py-2 text-right font-semibold text-amber-600">Scope</th>
                    <th className="px-3 py-2 text-right font-semibold text-emerald-600">Bookings</th>
                    <th className="px-3 py-2 text-right font-semibold text-teal-600">Confirm Sale</th>
                    <th className="px-3 py-2 text-right font-semibold text-rose-600">Registry</th>
                    <th className="px-3 py-2 text-right font-semibold text-blue-600">Possession</th>
                    <th className="px-3 py-2 text-right font-semibold">Conv%</th>
                  </tr>
                </thead>
                <tbody>
                  {projectBreakdown.map((p, i) => (
                    <tr key={i} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="px-3 py-2 font-medium text-stone-800 whitespace-nowrap">{p.project?.length > 18 ? p.project.substring(0, 18) + '..' : p.project}</td>
                      <td className="px-3 py-2 text-right font-bold">{p.total?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-violet-600">{p.officeVisit?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-indigo-600">{p.projectVisit?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-amber-600">{p.scope?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{(p.bookings ?? p.done)?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-teal-600">{(p.confirmSale || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-rose-600">{(p.registry || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-blue-600">{(p.possession || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${p.conversionRate >= 5 ? 'bg-emerald-100 text-emerald-700' : p.conversionRate >= 2 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>
                          {p.conversionRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-stone-800 text-white font-bold text-xs">
                    <td className="px-3 py-2">TOTAL</td>
                    <td className="px-3 py-2 text-right">{projectBreakdown.reduce((s,p) => s+(p.total||0), 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{projectBreakdown.reduce((s,p) => s+(p.officeVisit||0), 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{projectBreakdown.reduce((s,p) => s+(p.projectVisit||0), 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{projectBreakdown.reduce((s,p) => s+(p.scope||0), 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{projectBreakdown.reduce((s,p) => s+((p.bookings ?? p.done)||0), 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{projectBreakdown.reduce((s,p) => s+(p.confirmSale||0), 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{projectBreakdown.reduce((s,p) => s+(p.registry||0), 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{projectBreakdown.reduce((s,p) => s+(p.possession||0), 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{totalLeads ? (projectBreakdown.reduce((s,p) => s+(p.done||0), 0) / totalLeads * 100).toFixed(1) : 0}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardHeader className="pb-1"><CardTitle className="text-sm font-semibold text-stone-800">Project Lead Distribution</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={projectBreakdown.map(p => ({
                name: p.project?.length > 12 ? p.project.substring(0, 12) + '..' : p.project,
                leads: p.total || 0, officeVisit: p.officeVisit || 0, projectVisit: p.projectVisit || 0,
                scope: p.scope || 0, bookings: (p.bookings ?? p.done) || 0, confirmSale: p.confirmSale || 0,
                registry: p.registry || 0, possession: p.possession || 0,
              }))} margin={{ bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#64748b' }} angle={-35} textAnchor="end" height={70} interval={0} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar isAnimationActive={false} dataKey="leads" fill="#0ea5e9" name="Leads" radius={[4,4,0,0]} />
                <Bar isAnimationActive={false} dataKey="officeVisit" fill="#8b5cf6" name="Office Visits" radius={[4,4,0,0]} />
                <Bar isAnimationActive={false} dataKey="projectVisit" fill="#6366f1" name="Project Visits" radius={[4,4,0,0]} />
                <Bar isAnimationActive={false} dataKey="scope" fill="#b45309" name="Scope" radius={[4,4,0,0]} />
                <Bar isAnimationActive={false} dataKey="bookings" fill="#10b981" name="Bookings" radius={[4,4,0,0]} />
                <Bar isAnimationActive={false} dataKey="confirmSale" fill="#14b8a6" name="Confirm Sale" radius={[4,4,0,0]} />
                <Bar isAnimationActive={false} dataKey="registry" fill="#f43f5e" name="Registry" radius={[4,4,0,0]} />
                <Bar isAnimationActive={false} dataKey="possession" fill="#1d4ed8" name="Possession" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Nagpur Phase Breakdown */}
      {nagpurPhaseBreakdown?.length > 0 && (
        <Card className="border-0 shadow-md overflow-hidden" data-testid="nagpur-phase-breakdown">
          <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-700 text-white py-3">
            <CardTitle className="text-sm font-semibold">Dream Nagpur City - Phase Wise Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-orange-50">
                    <th className="px-4 py-2.5 text-left font-semibold text-stone-700">Phase</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Total Leads</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-amber-600">Inprocess</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-blue-600">Hold</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-red-500">Cancel</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-emerald-600">Done</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-violet-600">Office Visit</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-indigo-600">Project Visit</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Scope</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Conv %</th>
                  </tr>
                </thead>
                <tbody>
                  {nagpurPhaseBreakdown.map((p, i) => (
                    <tr key={i} className="border-b border-stone-100 hover:bg-orange-50/50">
                      <td className="px-4 py-2.5 font-bold text-orange-700">{p.phase}</td>
                      <td className="px-4 py-2.5 text-right font-bold">{p.total?.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-amber-600">{p.inprocess?.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-blue-600">{p.hold?.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-red-500">{p.cancel?.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-700 font-semibold">{p.done?.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-violet-600">{p.officeVisit?.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-indigo-600">{p.projectVisit?.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right">{p.scope?.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${p.conversionRate >= 5 ? 'bg-emerald-100 text-emerald-700' : p.conversionRate >= 2 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>
                          {p.conversionRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-orange-800 text-white font-bold text-xs">
                    <td className="px-4 py-2.5">TOTAL</td>
                    <td className="px-4 py-2.5 text-right">{nagpurPhaseBreakdown.reduce((s,p)=>s+p.total,0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right">{nagpurPhaseBreakdown.reduce((s,p)=>s+p.inprocess,0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right">{nagpurPhaseBreakdown.reduce((s,p)=>s+p.hold,0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right">{nagpurPhaseBreakdown.reduce((s,p)=>s+p.cancel,0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right">{nagpurPhaseBreakdown.reduce((s,p)=>s+p.done,0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right">{nagpurPhaseBreakdown.reduce((s,p)=>s+p.officeVisit,0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right">{nagpurPhaseBreakdown.reduce((s,p)=>s+p.projectVisit,0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right">{nagpurPhaseBreakdown.reduce((s,p)=>s+p.scope,0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right">
                      {(nagpurPhaseBreakdown.reduce((s,p)=>s+p.done,0) / nagpurPhaseBreakdown.reduce((s,p)=>s+p.total,0) * 100).toFixed(1)}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* DM Performance */}
      <div className="grid grid-cols-1 gap-6">
        <Card className="border-0 shadow-md overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-white py-3">
            <CardTitle className="text-sm font-semibold">DM / Agent Performance (Land Projects)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-indigo-50">
                    <th className="px-3 py-2 text-left font-semibold">DM Name</th>
                    <th className="px-3 py-2 text-right font-semibold">Leads</th>
                    <th className="px-3 py-2 text-right font-semibold text-violet-600">Office Visits</th>
                    <th className="px-3 py-2 text-right font-semibold text-indigo-600">Project Visits</th>
                    <th className="px-3 py-2 text-right font-semibold text-amber-600">Scope</th>
                    <th className="px-3 py-2 text-right font-semibold text-emerald-600">Bookings</th>
                    <th className="px-3 py-2 text-right font-semibold text-teal-600">Confirm Sale</th>
                    <th className="px-3 py-2 text-right font-semibold text-rose-600">Registry</th>
                    <th className="px-3 py-2 text-right font-semibold text-blue-600">Possession</th>
                    <th className="px-3 py-2 text-right font-semibold">Conv%</th>
                  </tr>
                </thead>
                <tbody>
                  {dmPerformance.filter(d => d.name).map((d, i) => (
                    <tr key={i} className="border-b border-stone-100 hover:bg-indigo-50/50">
                      <td className="px-3 py-2 font-medium text-stone-800">{d.name}</td>
                      <td className="px-3 py-2 text-right font-bold">{d.total.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-violet-600">{d.officeVisit}</td>
                      <td className="px-3 py-2 text-right text-indigo-600">{d.projectVisit || 0}</td>
                      <td className="px-3 py-2 text-right text-amber-600">{d.scope}</td>
                      <td className="px-3 py-2 text-right text-emerald-600 font-semibold">{d.done}</td>
                      <td className="px-3 py-2 text-right text-teal-600">{d.confirmSale || 0}</td>
                      <td className="px-3 py-2 text-right text-rose-600">{d.registry || 0}</td>
                      <td className="px-3 py-2 text-right text-blue-600">{d.possession || 0}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${d.total && d.done/d.total >= 0.05 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {d.total ? (d.done / d.total * 100).toFixed(1) : 0}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {agentBreakdown && agentBreakdown.length > 0 && (
          <Card className="border-0 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-violet-600 to-purple-800 text-white py-3">
              <CardTitle className="text-sm font-semibold">Agent Wise Performance (Flat Projects)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-violet-50">
                      <th className="px-3 py-2 text-left font-semibold">Agent Name</th>
                      <th className="px-3 py-2 text-right font-semibold">Total Leads</th>
                      <th className="px-3 py-2 text-right font-semibold text-violet-600">Office Visits</th>
                      <th className="px-3 py-2 text-right font-semibold text-indigo-600">Project Visits</th>
                      <th className="px-3 py-2 text-right font-semibold text-amber-600">Scope</th>
                      <th className="px-3 py-2 text-right font-semibold text-emerald-600">Bookings</th>
                      <th className="px-3 py-2 text-right font-semibold text-teal-600">Confirm Sale</th>
                      <th className="px-3 py-2 text-right font-semibold text-blue-600">Possession</th>
                      <th className="px-3 py-2 text-right font-semibold">Conv%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentBreakdown.filter(a => a.name).map((a, i) => (
                      <tr key={i} className="border-b border-stone-100 hover:bg-violet-50/50">
                        <td className="px-3 py-2 font-medium text-stone-800">{a.name}</td>
                        <td className="px-3 py-2 text-right font-bold">{(a.count || a.total || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right text-violet-600">{a.officeVisit || 0}</td>
                        <td className="px-3 py-2 text-right text-indigo-600">{a.projectVisit || 0}</td>
                        <td className="px-3 py-2 text-right text-amber-600">{a.scope || 0}</td>
                        <td className="px-3 py-2 text-right text-emerald-600 font-semibold">{a.done || 0}</td>
                        <td className="px-3 py-2 text-right text-teal-600">{a.confirmSale || 0}</td>
                        <td className="px-3 py-2 text-right text-blue-600">{a.possession || 0}</td>
                        <td className="px-3 py-2 text-right">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${(a.count||a.total) && (a.done||0)/(a.count||a.total) >= 0.05 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {(a.count||a.total) ? ((a.done||0) / (a.count||a.total) * 100).toFixed(1) : 0}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-0 shadow-md">
          <CardHeader className="pb-1"><CardTitle className="text-sm font-semibold text-stone-800">Top Remark Categories</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={remarkBreakdown.slice(0, 12)} margin={{ top: 5, right: 20, left: 10, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="remark" tick={{ fontSize: 10, fill: '#64748b' }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                <Tooltip />
                <Bar isAnimationActive={false} dataKey="count" name="Count" radius={[4,4,0,0]} maxBarSize={40}>
                  {remarkBreakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend */}
      {monthlyTrend?.length > 0 && (
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-1"><CardTitle className="text-sm font-semibold text-stone-800">Monthly Lead Trend</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={monthlyTrend} margin={{ bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                <Tooltip />
                <Legend content={() => (
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px', marginTop: 8 }}>
                    {[
                      { name: 'Leads', color: '#0ea5e9' }, { name: 'Office Visits', color: '#8b5cf6' },
                      { name: 'Project Visits', color: '#6366f1' }, { name: 'Scope', color: '#f59e0b' },
                      { name: 'Bookings', color: '#10b981' }, { name: 'Confirm Sale', color: '#14b8a6' },
                      { name: 'Registry', color: '#f43f5e' }, { name: 'Possession', color: '#3b82f6' },
                    ].map(m => (
                      <div key={m.name} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b' }}>
                        <div style={{ width: 12, height: 12, backgroundColor: m.color, borderRadius: 2, flexShrink: 0 }} />
                        <span>{m.name}</span>
                      </div>
                    ))}
                  </div>
                )} />
                <Bar isAnimationActive={false} dataKey="total" fill="#0ea5e9" name="Leads" radius={[4,4,0,0]} />
                <Bar isAnimationActive={false} dataKey="officeVisit" fill="#8b5cf6" name="Office Visits" radius={[4,4,0,0]} />
                <Bar isAnimationActive={false} dataKey="projectVisit" fill="#6366f1" name="Project Visits" radius={[4,4,0,0]} />
                <Bar isAnimationActive={false} dataKey="scope" fill="#b45309" name="Scope" radius={[4,4,0,0]} />
                <Bar isAnimationActive={false} dataKey="bookings" fill="#10b981" name="Bookings" radius={[4,4,0,0]} />
                <Bar isAnimationActive={false} dataKey="confirmSale" fill="#14b8a6" name="Confirm Sale" radius={[4,4,0,0]} />
                <Bar isAnimationActive={false} dataKey="registry" fill="#f43f5e" name="Registry" radius={[4,4,0,0]} />
                <Bar isAnimationActive={false} dataKey="possession" fill="#1d4ed8" name="Possession" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Reference Source Breakdown */}
      {referenceSource?.length > 0 && (
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-1"><CardTitle className="text-sm font-semibold text-stone-800">Reference Source Breakdown</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie isAnimationActive={false} data={referenceSource.map(r => ({ name: r.source, value: r.count }))} cx="50%" cy="50%" innerRadius={50} outerRadius={100} paddingAngle={3} dataKey="value">
                  {referenceSource.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportsTab;
